/*
 * MOBILE APP EQUIVALENT COMPONENT: screens/StatementScreen/SettlementDetailScreen.tsx
 *
 * TODO: MixPanel Tracking - see commented code.
 */

import { LinearGradient } from 'expo-linear-gradient';
import { orderBy } from 'lodash';
import moment from 'moment-timezone';
import React, { useState, useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import {
  useSettlementBreakdownQuery,
  useSingleBillStatementQuery,
} from 'apollo/queries/autogenerate/hooks';
import {
  chargeDataFragment,
  SettlementBreakdownQuery,
} from 'apollo/queries/autogenerate/operations';
import {
  Charge,
  BillStatement,
  PaidOverdueBillStatement,
} from 'apollo/queries/autogenerate/schemas';
// import { SETTLEMENT_DETAILS_SCREEN } from 'shared/constants/mixpanel';
import Badge from 'shared/modules/common/components/atoms/Badge';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import ChargeItem from 'shared/modules/common/components/organisms/ChargeItem';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import ChargeMessage from 'shared/modules/usage/components/molecules/ChargeMessage';
import ListItem from 'shared/modules/usage/components/molecules/ListItem';
import ListItemDivider from 'shared/modules/usage/components/molecules/ListItemDivider';
import ListSection from 'shared/modules/usage/components/molecules/ListSection';
import { checkIsBillUsingAutoDebit } from 'shared/modules/usage/utils/utils';
import {
  formatMonthDate,
  formatToCurrency,
  formatYearMonth,
} from 'shared/utils/japanese';
// import { useMixpanelTrackPageView } from 'utils/hooks/use-mixpanel-track-page-view';

// const screen = SETTLEMENT_DETAILS_SCREEN;

const SettlementSummary = ({
  settlement,
  bill,
}: {
  settlement: Charge;
  bill: BillStatement;
}) => {
  const amount = settlement ? formatToCurrency(settlement.amount) : '¥-';

  const pushedAt = settlement
    ? formatMonthDate(settlement.pushedAt, 'M月D日')
    : '-';

  // Payment method and amount label
  let paymentMethod = '-';
  let amountLabel = 'ご請求金額'; // Billed amount

  if (settlement.isPayNowSettlement) {
    paymentMethod = 'コンビニ払い'; // Convenience store payment
    amountLabel = 'すぐ払い金額'; // Immediate payment amount
  } else if (settlement.isVanTransferSettlement) {
    paymentMethod = '銀行振込'; // Bank transfer
    amountLabel = 'お支払い金額'; // Payment amount
  } else {
    if (bill) {
      paymentMethod = checkIsBillUsingAutoDebit(bill)
        ? '口座振替' // Auto debit
        : 'コンビニ払い'; // Convenience store payment
    }
  }

  // Show "paid" badge only when settlement is not PayNow or Bank Transfer
  const shouldShowPaidBadge =
    !!settlement &&
    !settlement.isPayNowSettlement &&
    !settlement.isVanTransferSettlement;

  return (
    <View>
      <View style={styles.billSummaryContainer}>
        <Text style={styles.caption02}>{amountLabel}</Text>
        <View style={styles.billAmountWrapper}>
          <Text style={styles.billAmount}>{amount}</Text>
          {shouldShowPaidBadge && (
            <Badge title="お支払い済み" variant="success" />
          )}
        </View>
        <View style={styles.divider} />

        {/* Paid date */}
        <View style={styles.row}>
          <Text style={styles.caption02}>お支払い日</Text>
          <Text style={typography.caption01}>{pushedAt}</Text>
        </View>
        {/* Settlement type */}
        <View style={styles.row}>
          <Text style={styles.caption02}>お支払い方法</Text>
          <Text style={typography.caption01}>{paymentMethod}</Text>
        </View>
      </View>
    </View>
  );
};

const combineCharges = (
  settlementBreakdown?: SettlementBreakdownQuery['settlementBreakdown']
) => {
  if (settlementBreakdown) {
    const {
      currentUsageCharges,
      outstandingBillStatementCharges,
      postSettlementCharges,
    } = settlementBreakdown;

    let allCharges: chargeDataFragment[] = [
      ...outstandingBillStatementCharges.charges,
      ...currentUsageCharges.charges,
    ];

    postSettlementCharges.forEach(({ charges }) => {
      allCharges = [...allCharges, ...charges];
    });

    // Oldest to latest
    return orderBy(allCharges, 'pushedAt', 'asc');
  }

  return [];
};

const getBillStatementIdFromSettlement = (
  settlement: Charge | undefined
): string => {
  const metadataSource = settlement ? settlement.metadata.source : '';

  if (metadataSource.includes('cbs_')) {
    return metadataSource;
  }

  return '';
};

function SettlementDetail({
  settlement,
  onChargeItemPress,
  onBillItemPress,
}: {
  settlement: Charge;
  onChargeItemPress: (charge: Charge) => void;
  onBillItemPress: (bill: PaidOverdueBillStatement) => void;
}) {
  // TODO: MixPanel Tracking
  // useMixpanelTrackPageView({ screen });

  const billStatementId = getBillStatementIdFromSettlement(settlement);

  const { data: settlementBreakdown, error } = useSettlementBreakdownQuery({
    variables: {
      settlementChargeId: settlement ? settlement.id : '',
    },
    fetchPolicy: 'cache-and-network',
  });
  const { data: billData } = useSingleBillStatementQuery({
    skip: !billStatementId,
    variables: {
      id: billStatementId,
    },
  });

  const overdueBillStatements =
    settlementBreakdown?.settlementBreakdown?.overdueBillStatements || [];

  const sortedOverdueBillStatements = useMemo(
    () => orderBy(overdueBillStatements, 'dueAt', 'asc'),
    [settlementBreakdown]
  );
  const allCharges = useMemo(
    () => combineCharges(settlementBreakdown?.settlementBreakdown),
    [settlementBreakdown]
  );

  const allItemsLength = allCharges.length + overdueBillStatements.length;

  return (
    <View style={styles.container}>
      <ScrollView>
        <SettlementSummary
          settlement={settlement as Charge}
          bill={billData?.billStatement as BillStatement}
        />

        <ListSection
          title="ご利用明細"
          emptyMessage={!allItemsLength ? 'ご利用はありません' : ''}
          errorMessage={error ? 'ネットワークエラー' : ''}
          style={styles.listSection}
        >
          <>
            {allCharges.map((charge, index) => (
              <View key={charge.id}>
                <ChargeItem charge={charge} onPress={onChargeItemPress} />
                {index !== allItemsLength - 1 && <ListItemDivider />}
              </View>
            ))}

            {sortedOverdueBillStatements.map((bill, index) => (
              <View key={bill.billStatementId}>
                <ListItem
                  iconName="settlement"
                  title={`${formatYearMonth(
                    bill.billingCycle
                  )}分 (お支払い済み)`}
                  amount={bill.paidAmount}
                  onPress={() => onBillItemPress(bill)}
                />
                {index !== allItemsLength - 1 && <ListItemDivider />}
              </View>
            ))}
          </>
        </ListSection>
        <View style={styles.messageContainer}>
          <ChargeMessage />
        </View>
      </ScrollView>
      <LinearGradient
        style={styles.footer}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 0.24 }}
        colors={['rgba(238, 240, 242, 0)', 'rgba(238, 240, 242, 1)']}
      >
        <Text style={styles.footerText}>
          キャンセルがある場合は、{'\n'}
          合計金額と請求金額が異なることがあります。
        </Text>
      </LinearGradient>
    </View>
  );
}

type Props = {
  charge?: Charge;
  open: boolean;
  onCloseButtonPress: () => void;
  onChargeItemPress: (charge: Charge) => void;
  onBillItemPress: (bill: PaidOverdueBillStatement) => void;
};

export default function SettlementDialog(props: Props) {
  if (!props.charge) {
    return null;
  }

  const charge = props.charge;
  let dialogTitle = '';

  if (charge.isPayNowSettlement) {
    dialogTitle = 'すぐ払い明細'; // Immediate payment details
  } else {
    if (charge.isVanTransferSettlement) {
      dialogTitle = '支払い明細'; // Payment slip
    } else if (charge.paidBillingCycle) {
      dialogTitle = `${moment(charge.paidBillingCycle)
        .tz('Asia/Tokyo')
        .format('YYYY年MM月分')}のご請求`;
    }
  }

  return (
    <Dialog
      title={dialogTitle}
      onCloseButtonPress={props.onCloseButtonPress}
      open={props.open}
      style={styles.container}
      id="settlement-dialog"
    >
      <View style={styles.body}>
        <SettlementDetail
          settlement={charge}
          onChargeItemPress={props.onChargeItemPress}
          onBillItemPress={props.onBillItemPress}
        />
      </View>
    </Dialog>
  );
}

export function useSettlementDialog() {
  const [charge, setCharge] = useState<Charge>();
  const [isOpen, setIsOpen] = useState(false);

  const open = (_charge: Charge) => {
    setCharge(_charge);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  return {
    charge,
    isOpen,
    open,
    close,
  };
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 0,
    maxHeight: 624,
  },
  body: {
    marginHorizontal: -24,
    backgroundColor: '#f6f8fa',
  },
  row: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  caption02: {
    ...typography.caption02,
    fontWeight: '400',
  },
  billSummaryContainer: {
    padding: 24,
    backgroundColor: colors.white,
  },
  billAmountWrapper: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  billAmount: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.greyDark,
    marginRight: 16,
  },
  divider: {
    flex: 1,
    height: 1,
    maxHeight: 1,
    backgroundColor: colors.greyLight,
    marginVertical: 16,
  },
  listSection: {
    margin: 16,
  },
  messageContainer: {
    paddingHorizontal: 16,
    marginBottom: 80,
  },
  footer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    paddingTop: 32,
    paddingHorizontal: 16,
    paddingBottom: 24,
  },
  footerText: {
    ...typography.caption02,
    fontWeight: '400',
    textAlign: 'center',
    color: colors.grey,
  },
});
